/**
 * B2 · Etapa 1 — COMPAÑEROS del aula (`academy.classroom.roster`).
 *
 * Contrato de privacidad (Q2 del plan), y está en el servidor, no aquí: nombre
 * para mostrar, avatar y rol. NUNCA correo, cédula, teléfono, estado de
 * matrícula ni notas. `GET /api/cohorts/:id/roster` sirve la vista de compañero
 * con esos campos ya redactados, así que esta pantalla no puede enseñar de más
 * aunque quisiera.
 *
 * Se sirve por API y no por réplica a propósito: es identidad de TERCEROS, y el
 * alcance correcto («la gente de ESTA cohorte») no cabe en un `with:` plano
 * (`docs/DATA_ACCESS_POLICY.md` §2).
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { GraduationCap, Users } from "lucide-solid";
import type { OrgIdentity } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { useCohortIdentities } from "../../hooks/use-org-identities";

/** Cuántas iniciales lleva el avatar de reserva. */
const MAX_INITIALS = 2;

/** Iniciales para el avatar de reserva (nunca el correo: no lo tenemos). */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "·";
  }
  // `codePointAt` y no `[0]`: un nombre que empieza por un carácter fuera del
  // plano básico (o por un emoji) se partiría en dos mitades ilegibles.
  return parts
    .slice(0, MAX_INITIALS)
    .map((p) => String.fromCodePoint(p.codePointAt(0) ?? 0).toUpperCase())
    .join("");
}

/** ¿Esta persona da clase en la cohorte? (rol de organización). */
function isTeaching(identity: OrgIdentity): boolean {
  return identity.role === "teacher" || identity.role === "owner" || identity.role === "org_admin";
}

export const ClassroomRoster: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  const roster = useCohortIdentities(() => props.cohortId);

  const people = () =>
    [...roster.identities().values()].toSorted(
      (a, b) =>
        Number(isTeaching(b)) - Number(isTeaching(a)) || (a.name ?? "").localeCompare(b.name ?? ""),
    );

  return (
    <section class="space-y-3 rounded-2xl border border-border p-5" data-testid="classroom-roster">
      <h2 class="flex items-center gap-2 font-display text-lg font-bold">
        <Users class="h-5 w-5 text-accent" /> {t(msg`Classmates`)}
      </h2>
      <Show when={!roster.isLoading()} fallback={<Skeleton class="h-16 w-full rounded-xl" />}>
        <Show
          when={people().length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="classroom-roster-empty">
              {t(msg`Nobody else has joined this cohort yet.`)}
            </p>
          }
        >
          <ul class="flex flex-wrap gap-2">
            <For each={people()}>
              {(person) => (
                <li
                  class="flex items-center gap-2 rounded-full bg-muted/50 py-1 pe-3 ps-1 text-xs"
                  data-testid="classroom-roster-person"
                  data-user-id={person.userId}
                >
                  <Show
                    when={person.image}
                    fallback={
                      <span class="flex h-6 w-6 items-center justify-center rounded-full bg-accent/15 text-[10px] font-bold text-accent">
                        {initialsOf(person.name)}
                      </span>
                    }
                  >
                    {(image) => (
                      <img src={image()} alt="" class="h-6 w-6 rounded-full object-cover" />
                    )}
                  </Show>
                  <span class="font-medium">{person.name ?? t(msg`Classmate`)}</span>
                  <Show when={isTeaching(person)}>
                    <GraduationCap class="h-3.5 w-3.5 text-primary" aria-label={t(msg`Teacher`)} />
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
      <p class="text-xs text-muted-foreground">
        {t(msg`Only names and photos are shared with your classmates.`)}
      </p>
    </section>
  );
};
